import { useEffect } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider } from "react-redux";
import { BrowserRouter, Outlet, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";

import { ApiKeyDetails } from "@/components/ApiKeyDetails/ApiKeyDetails";
import { EmbeddedWalletNoticesProvider } from "@/components/EmbeddedWalletNoticesProvider";
import { GlobalBanner } from "@/components/GlobalBanner";
import { InnerPage } from "@/components/InnerPage";
import { KeycloakUserSession } from "@/components/KeycloakUserSession";
import { PrivateRoute } from "@/components/PrivateRoute";
import { SessionTokenRefresher } from "@/components/SessionTokenRefresher";
import { UserSession } from "@/components/UserSession";
import { WalletPrivateRoute } from "@/components/WalletPrivateRoute";
import { WalletSession } from "@/components/WalletSession";
import { WalletSessionRefresher } from "@/components/WalletSessionRefresher";

import { Analytics } from "@/pages/Analytics";
import { ApiKeys } from "@/pages/ApiKeys";
import { AuditLogs } from "@/pages/AuditLogs";
import { DataExplorer } from "@/pages/DataExplorer";
import { DisbursementDetails } from "@/pages/DisbursementDetails";
import { DisbursementDraftDetails } from "@/pages/DisbursementDraftDetails";
import { Disbursements } from "@/pages/Disbursements";
import { DisbursementsDrafts } from "@/pages/DisbursementsDrafts";
import { DisbursementsNew } from "@/pages/DisbursementsNew";
import { DistributionAccount } from "@/pages/DistributionAccount";
import { EmbeddedWallet } from "@/pages/EmbeddedWallet";
import { EmbeddedWalletHome } from "@/pages/EmbeddedWalletHome";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { Help } from "@/pages/Help";
import { Home } from "@/pages/Home";
import { MFAuth } from "@/pages/MFAuth";
import { MultisigManage } from "@/pages/MultisigManage";
import { MultisigSetup } from "@/pages/MultisigSetup";
import { NotFound } from "@/pages/NotFound";
import { Notifications } from "@/pages/Notifications";
import { PaymentDetails } from "@/pages/PaymentDetails";
import { Payments } from "@/pages/Payments";
import { Profile } from "@/pages/Profile";
import { ReceiverDetails } from "@/pages/ReceiverDetails";
import { ReceiverDetailsEdit } from "@/pages/ReceiverDetailsEdit";
import { Receivers } from "@/pages/Receivers";
import { SigninOidc } from "@/pages/Redirect";
import { Reports } from "@/pages/Reports";
import { ResetPassword } from "@/pages/ResetPassword";
import { SetNewPassword } from "@/pages/SetNewPassword";
import { Settings } from "@/pages/Settings";
import { SignIn } from "@/pages/SignIn";
import { Unauthorized } from "@/pages/Unauthorized";
import { WalletConnectDebugPage } from "@/pages/WalletConnectDebugPage";
import { WalletProviders } from "@/pages/WalletProviders";

import { MULTISIG_FEATURE_FLAGS, isMultisigFeatureEnabled } from "@/constants/multisigFeatureFlags";
import { ONBOARDING_ROLES, Routes } from "@/constants/settings";

import { useAppConfig } from "@/hooks/useAppConfig";

import { Onboarding } from "./pages/Onboarding";

import { KeycloakAuthProvider, useKeycloakAuth } from "@/contexts/KeycloakAuthContext";
import GitInfo from "@/generated/gitInfo";
import { store } from "@/store";

import "@/styles/styles.scss";

/** Renders Reports when reporting is enabled for the tenant; otherwise 404. */
const ReportsPageGate = () => {
  const { isReportingEnabled } = useAppConfig();
  if (!isReportingEnabled) return <NotFound />;
  return (
    <PrivateRoute>
      <InnerPage isNarrow={true}>
        <Reports />
      </InnerPage>
    </PrivateRoute>
  );
};

/** Renders Data Explorer when enabled for the tenant (VIA only); otherwise 404. */
const DataExplorerPageGate = () => {
  const { isDataExplorerEnabled } = useAppConfig();
  if (!isDataExplorerEnabled) return <NotFound />;
  return (
    <PrivateRoute>
      <InnerPage>
        <DataExplorer />
      </InnerPage>
    </PrivateRoute>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const WalletLayout = () => {
  return (
    <EmbeddedWalletNoticesProvider>
      <WalletSession />
      <WalletSessionRefresher />
      <Outlet />
    </EmbeddedWalletNoticesProvider>
  );
};

const AppRoutes = () => {
  const { isKeycloakMode } = useKeycloakAuth();

  return (
    <>
      {isKeycloakMode ? <KeycloakUserSession /> : <UserSession />}
      {!isKeycloakMode && <SessionTokenRefresher />}
      <BrowserRouter>
        <GlobalBanner />
        <RouterRoutes>
          {/* Sign in — in Keycloak mode, redirect to dashboard */}
          <Route
            path="/"
            element={
              isKeycloakMode ? (
                <Navigate to={Routes.HOME} replace />
              ) : (
                <InnerPage isCardLayout>
                  <SignIn />
                </InnerPage>
              )
            }
          />
          {/* Forgot password — hidden in Keycloak mode */}
          {!isKeycloakMode && (
            <Route
              path={Routes.FORGOT_PASSWORD}
              element={
                <InnerPage isCardLayout>
                  <ForgotPassword />
                </InnerPage>
              }
            />
          )}
          {/* Reset password — hidden in Keycloak mode */}
          {!isKeycloakMode && (
            <Route
              path={Routes.RESET_PASSWORD}
              element={
                <InnerPage isCardLayout>
                  <ResetPassword />
                </InnerPage>
              }
            />
          )}
          {/* Reset password (authenticated user) — hidden in Keycloak mode */}
          {!isKeycloakMode && (
            <Route
              path={Routes.SET_NEW_PASSWORD}
              element={
                <InnerPage isCardLayout>
                  <SetNewPassword />
                </InnerPage>
              }
            />
          )}
          {/* 2FA Verification — hidden in Keycloak mode */}
          {!isKeycloakMode && (
            <Route
              path={Routes.MFA}
              element={
                <InnerPage isCardLayout>
                  <MFAuth />
                </InnerPage>
              }
            />
          )}
          {/* Onboarding */}
          <Route
            path={Routes.ONBOARDING}
            element={
              <PrivateRoute acceptedRoles={ONBOARDING_ROLES}>
                <InnerPage hideSidebar>
                  <Onboarding />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Home */}
          <Route
            path={Routes.HOME}
            element={
              <PrivateRoute>
                <InnerPage>
                  <Home />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Notifications */}
          {isMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.NOTIFICATIONS) && (
            <Route
              path={Routes.NOTIFICATIONS}
              element={
                <PrivateRoute>
                  <InnerPage>
                    <Notifications />
                  </InnerPage>
                </PrivateRoute>
              }
            />
          )}
          {/* Multisig setup */}
          {isMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.ENROLLMENT) && (
            <Route
              path={Routes.MULTISIG_SETUP}
              element={
                <PrivateRoute acceptedRoles={["owner"]}>
                  <InnerPage>
                    <MultisigSetup />
                  </InnerPage>
                </PrivateRoute>
              }
            />
          )}
          {isMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.OPT_OUT) && (
            <Route
              path={Routes.MULTISIG_MANAGE}
              element={
                <PrivateRoute acceptedRoles={["owner"]}>
                  <InnerPage>
                    <MultisigManage />
                  </InnerPage>
                </PrivateRoute>
              }
            />
          )}
          {/* Disbursements */}
          <Route
            path={Routes.DISBURSEMENTS}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <Disbursements />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.DISBURSEMENTS}/:id`}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <DisbursementDetails />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.DISBURSEMENT_NEW}
            element={
              <PrivateRoute acceptedRoles={["owner", "financial_controller", "initiator"]}>
                <InnerPage isNarrow={true}>
                  <DisbursementsNew />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.DISBURSEMENT_DRAFTS}
            element={
              <PrivateRoute
                acceptedRoles={["owner", "financial_controller", "initiator", "approver"]}
              >
                <InnerPage isNarrow={true}>
                  <DisbursementsDrafts />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.DISBURSEMENT_DRAFTS}/:id`}
            element={
              <PrivateRoute
                acceptedRoles={["owner", "financial_controller", "initiator", "approver"]}
              >
                <InnerPage isNarrow={true}>
                  <DisbursementDraftDetails />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Receivers */}
          <Route
            path={Routes.RECEIVERS}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <Receivers />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.RECEIVERS}/:id`}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <ReceiverDetails />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.RECEIVERS_EDIT}/:id`}
            element={
              <PrivateRoute acceptedRoles={["owner", "financial_controller"]}>
                <InnerPage isNarrow={true}>
                  <ReceiverDetailsEdit />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Payments */}
          <Route
            path={`${Routes.PAYMENTS}/:id`}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <PaymentDetails />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PAYMENTS}
            element={
              <PrivateRoute
                acceptedRoles={[
                  "owner",
                  "financial_controller",
                  "business",
                  "initiator",
                  "approver",
                ]}
              >
                <InnerPage>
                  <Payments />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Distribution Account */}
          <Route
            path={Routes.DISTRIBUTION_ACCOUNT}
            element={
              <PrivateRoute>
                <InnerPage isNarrow={true}>
                  <DistributionAccount />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Wallet Providers */}
          <Route
            path={Routes.WALLET_PROVIDERS}
            element={
              <PrivateRoute>
                <InnerPage isNarrow={true}>
                  <WalletProviders />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Analytics */}
          <Route
            path={Routes.ANALYTICS}
            element={
              <PrivateRoute>
                <InnerPage>
                  <Analytics />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Reports */}
          <Route path={Routes.REPORTS} element={<ReportsPageGate />} />
          {/* Audit Logs */}
          <Route
            path={Routes.AUDIT_LOGS}
            element={
              <PrivateRoute acceptedRoles={["owner", "financial_controller"]}>
                <InnerPage>
                  <AuditLogs />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Data Explorer — conditionally rendered via app-config */}
          <Route path={Routes.DATA_EXPLORER} element={<DataExplorerPageGate />} />
          {/* Api Keys */}
          <Route
            path={Routes.API_KEYS}
            element={
              <PrivateRoute acceptedRoles={["owner", "developer"]}>
                <InnerPage>
                  <ApiKeys />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Api Key Details */}
          <Route
            path={`${Routes.API_KEYS}/:id`}
            element={
              <PrivateRoute acceptedRoles={["owner", "developer"]}>
                <InnerPage>
                  <ApiKeyDetails />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Profile */}
          <Route
            path={Routes.PROFILE}
            element={
              <PrivateRoute>
                <InnerPage isNarrow={true}>
                  <Profile />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Settings */}
          <Route
            path={Routes.SETTINGS}
            element={
              <PrivateRoute acceptedRoles={["owner"]}>
                <InnerPage>
                  <Settings />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Help */}
          <Route
            path={Routes.HELP}
            element={
              <PrivateRoute>
                <InnerPage isNarrow={true}>
                  <Help />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* Unauthorized */}
          <Route
            path={Routes.UNAUTHORIZED}
            element={
              <PrivateRoute>
                <InnerPage>
                  <Unauthorized />
                </InnerPage>
              </PrivateRoute>
            }
          />
          {/* 404 */}
          <Route
            path="*"
            element={
              <PrivateRoute>
                <InnerPage>
                  <NotFound />
                </InnerPage>
              </PrivateRoute>
            }
          />
          <Route path="/signin-oidc" element={<SigninOidc />} />
          {/* TODO: to be removed after UI design landed on screen  */}
          {isMultisigFeatureEnabled(MULTISIG_FEATURE_FLAGS.WALLET_CONNECT) && (
            <Route path={Routes.WALLET_SESSION} element={<WalletConnectDebugPage />} />
          )}
          {/* Embedded Wallet Routes */}
          <Route element={<WalletLayout />}>
            <Route
              path={Routes.WALLET}
              element={
                <InnerPage isCardLayout>
                  <EmbeddedWallet />
                </InnerPage>
              }
            />
            <Route
              path={Routes.WALLET_HOME}
              element={
                <WalletPrivateRoute>
                  <InnerPage isCardLayout>
                    <EmbeddedWalletHome />
                  </InnerPage>
                </WalletPrivateRoute>
              }
            />
          </Route>
        </RouterRoutes>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

export const App = () => {
  useEffect(() => {
    // Git commit hash
    console.log("current commit hash: ", GitInfo.commitHash);
    console.log("version: ", GitInfo.version);
  }, []);

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <KeycloakAuthProvider>
          <AppRoutes />
        </KeycloakAuthProvider>
      </QueryClientProvider>
    </Provider>
  );
};
